import { EveRoutes } from '@ver-uds/e-verify';
import { Alert, Breadcrumb } from '@ver-uds/uswds-react';
import { AccountType } from 'eve-account-client';
import React from 'react';
import { generatePath, Link, useLocation, useParams } from 'react-router-dom';
import LoaderIcon from 'uswds/dist/img/loader.svg';
import { useEnvironment } from '../../context/Environment/EnvironmentContext';
import FullWidthLayout from '../../layouts/FullWidthLayout';
import { useAccountQuery, useMouDocumentQuery, useSignedMouDocumentQuery, useTosDocumentQuery } from '../../services';
import { renderTestingComponent } from '../../utils/envtoggle';
import AuthFileLink from '../AuthFileLink';
import CompanyAccess from '../CompanyAccess/CompanyAccess';
import CompanyInformation from '../CompanyInformation';
import CompanyLocations from '../CompanyLocations';
import Contacts from '../Contacts';
import EmployerCategory from '../EmployerCategory';
import ErrorBar from '../ErrorBar';
import HiringSites from '../HiringSites';
import AccountStatusTag from './AccountStatusTag';

export type ChildProfileState =
  | null
  | undefined
  | {
      mouUploadSuccess: boolean;
    };

const fetchPageData = (accountType: AccountType | undefined) => {
  switch (accountType) {
    case AccountType.CLIENT:
      return {
        documentTitle: 'Client Company',
        breadcrumbParent: 'Client Companies',
        breadcrumbParentUrl: EveRoutes.COMPANY_CLIENTS_VIEW,
        editEmployerCategoryUrlPath: EveRoutes.COMPANY_CLIENTS_CLIENT_EMPLOYER_CATEGORY_EDIT,
        editCompanyInformationUrlPath: EveRoutes.COMPANY_CLIENTS_CLIENT_COMPANY_INFO_EDIT,
        editHiringSitesUrlPath: EveRoutes.COMPANY_CLIENTS_CLIENT_HIRING_SITES_EDIT,
        editCompanyAddressesUrlPath: EveRoutes.COMPANY_CLIENTS_CLIENT_ADDRESS_EDIT,
      };
    case AccountType.CHILD:
    default:
      return {
        documentTitle: 'Company Location',
        breadcrumbParent: 'Company Locations',
        breadcrumbParentUrl: EveRoutes.CORPORATE_COMPANY_LOCATIONS_VIEW,
        editEmployerCategoryUrlPath: EveRoutes.CORPORATE_COMPANY_LOCATIONS_CHILD_EMPLOYER_CATEGORY_EDIT,
        editCompanyInformationUrlPath: EveRoutes.CORPORATE_COMPANY_LOCATIONS_CHILD_COMPANY_INFO_EDIT,
        editHiringSitesUrlPath: EveRoutes.CORPORATE_COMPANY_LOCATIONS_CHILD_HIRING_SITES_EDIT,
        editCompanyAddressesUrlPath: EveRoutes.CORPORATE_COMPANY_LOCATIONS_CHILD_ADDRESS_EDIT,
      };
  }
};

const ChildProfile = (): JSX.Element => {
  const { state } = useLocation<ChildProfileState>();
  const location = useLocation();
  const { subAccountId } = useParams<{ subAccountId: string }>();
  const errorMessage = location.state && typeof location.state === 'string' ? String(location.state) : undefined;

  const { data: accountData, isLoading } = useAccountQuery(subAccountId);
  const companyInfoData = accountData?.companyInfo;

  const companyName = companyInfoData?.companyName;

  const showMouUpload = !accountData?.mouSignedDate && accountData?.type === AccountType.CLIENT;

  const accountStatus = accountData && accountData.status;
  const { AUTH_CLIENTID } = useEnvironment();
  const isClientWithSignedMou = accountData?.mouSignedDate && accountData?.type === AccountType.CLIENT;

  const {
    documentTitle,
    breadcrumbParent,
    breadcrumbParentUrl,
    editEmployerCategoryUrlPath,
    editCompanyInformationUrlPath,
    editHiringSitesUrlPath,
    editCompanyAddressesUrlPath,
  } = fetchPageData(accountData?.type);

  const linkToSignedMou = (
    <AuthFileLink
      linkText="View MOU Signature Page"
      query={useSignedMouDocumentQuery}
      queryArg={subAccountId}
      aria-label="View MOU Signature Page in a new tab"
    />
  );

  const linkToCurrentMou = (
    <AuthFileLink
      linkText="View Current MOU"
      query={useMouDocumentQuery}
      queryArg={subAccountId}
      aria-label="View Current MOU in a new tab"
    />
  );

  const linkToMyI9TOS = (
    <AuthFileLink
      linkText="View E-Verify+ Terms of Service"
      query={useTosDocumentQuery}
      queryArg={subAccountId}
      aria-label="View E-Verify+ Terms of Service in a new tab"
    />
  );

  const linkToMou = isClientWithSignedMou ? (
    <>
      {linkToCurrentMou}
      <br />
      {linkToSignedMou}
    </>
  ) : (
    <>{linkToCurrentMou}</>
  );

  return (
    <FullWidthLayout title={companyName ? `${documentTitle}: ${companyName}` : documentTitle}>
      {isLoading ? (
        <div className="margin-y-4">
          Loading… <img src={LoaderIcon} alt="" className="height-2" />
        </div>
      ) : (
        <section className="padding-y-4">
          <Breadcrumb className="padding-y-0">
            <Link to={breadcrumbParentUrl}>{breadcrumbParent}</Link>
            {companyName}
          </Breadcrumb>
          <h1 className="margin-top-1">
            {companyName ? `${companyName} Profile` : 'Profile'}
            {accountStatus && (
              <AccountStatusTag size="large" className="margin-left-105 text-middle" status={accountStatus} />
            )}
          </h1>
          <ErrorBar errorMessage={errorMessage} />
          {state?.mouUploadSuccess && (
            <div className="tablet:grid-col-12 desktop:grid-col-9">
              <Alert status="success" slim>
                <Alert.Text>You’ve successfully uploaded the MOU Signature Page.</Alert.Text>{' '}
              </Alert>
            </div>
          )}
          {showMouUpload && (
            <div className="tablet:grid-col-12 desktop:grid-col-9">
              <Alert status="info" slim>
                <Alert.Text>
                  Before you can create cases on behalf of your client, the MOU Signatory you added during enrollment
                  must electronically sign the Memorandum of Understanding (MOU) or provide a signed signature page for
                  you to upload on their behalf.
                </Alert.Text>{' '}
              </Alert>
            </div>
          )}
          <CompanyInformation
            editUrl={generatePath(editCompanyInformationUrlPath, { subAccountId })}
            companyInfoData={companyInfoData}
            accountData={accountData}
          />
          <hr />
          <EmployerCategory
            editUrl={generatePath(editEmployerCategoryUrlPath, { subAccountId })}
            employerCategory={companyInfoData?.employerCategory}
          />

          {renderTestingComponent(AUTH_CLIENTID) && accountData?.type === AccountType.CLIENT && (
            <>
              <hr />
              <Contacts />
            </>
          )}

          <hr />
          <div className="grid-row grid-gap">
            <CompanyLocations
              accountData={accountData}
              editUrl={generatePath(editCompanyAddressesUrlPath, { subAccountId })}
            />
            <hr className="tablet:display-none grid-col-12 margin-x-1" />
            <HiringSites editUrl={generatePath(editHiringSitesUrlPath, { subAccountId })} />
          </div>
          <hr />
          <CompanyAccess
            accountData={accountData}
            linkToMou={linkToMou}
            linkToMyI9TOS={linkToMyI9TOS}
            showMyI9={accountData?.type === AccountType.CHILD}
          />
        </section>
      )}
    </FullWidthLayout>
  );
};

export default ChildProfile;
